import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PoweredBy from "../components/PoweredBy";
import {
  XCircle,
  Loader2,
  AlertCircle,
  Calendar,
  Clock,
  Users,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

interface ReservationData {
  confirmationCode: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  restaurantName: string;
}

export default function CancelReservation() {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    const getReservation = async () => {
      try {
        const res = await fetch(`${API_URL}/public/reservations/${code}`);
        const data = await res.json();

        if (!res.ok) {
          setError(data.message || "Reserva no encontrada");
          return;
        }

        if (data.reservation.status === "cancelled") {
          setCancelled(true);
        }

        setReservation(data.reservation);
      } catch (err) {
        setError("Error de conexión. Inténtalo de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      getReservation();
    }
  }, [code]);

  const handleCancel = async () => {
    setConfirming(true);
    try {
      const res = await fetch(`${API_URL}/public/reservations/${code}/cancel`, {
        method: "POST",
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || "Error al cancelar la reserva");
        return;
      }

      setCancelled(true);
    } catch (err) {
      setError("Error de conexión. Inténtalo de nuevo más tarde.");
    } finally {
      setConfirming(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-gray-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando reserva...</p>
        </div>
      </div>
    );
  }

  if (error && !reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-gray-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Reserva cancelada
          </h1>
          <p className="text-gray-600 mb-4">
            Tu reserva ha sido cancelada correctamente.
          </p>
          {reservation && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
              <p className="text-sm text-gray-600">
                <strong>{reservation.restaurantName}</strong>
                <br />
                {formatDate(reservation.date)} a las {reservation.time}h
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-4">
            Esperamos verte en otra ocasión.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Cancelar reserva
          </h1>
          <p className="text-gray-600">
            ¿Estás seguro de que quieres cancelar tu reserva?
          </p>
        </div>

        {reservation && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 mb-6">
            <h2 className="font-semibold text-gray-800 text-lg">
              {reservation.restaurantName}
            </h2>
            <div className="space-y-2 text-gray-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="capitalize">
                  {formatDate(reservation.date)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span>{reservation.time}h</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span>
                  {reservation.partySize}{" "}
                  {reservation.partySize === 1 ? "persona" : "personas"}
                </span>
              </div>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Código:</strong> {reservation.confirmationCode}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => window.close()}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            No, mantener
          </button>
          <button
            onClick={handleCancel}
            disabled={confirming}
            className="flex-1 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
          >
            {confirming ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Cancelando...
              </>
            ) : (
              "Sí, cancelar"
            )}
          </button>
        </div>

        <PoweredBy
          origen="cancelar-reserva"
          etiqueta="Con la tecnología de"
          className="mt-6"
        />
      </div>
    </div>
  );
}
